import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../services/apiClient';
import PoDetailModal, { PO_STATUS_COLORS, PoShipBits } from './PoDetailModal';

// A job's purchase orders, opened out (Pat, Sep 2026): the same information as the
// Administration → Purchase Orders page — number, description and notes, vendor, cost,
// who wrote it, when, status — plus a SHIP DATE column and a way to pull the Major POs
// to the front, because those are the ones a PM is chasing. A row opens the same PO
// window the Administration page opens (edit, status, ship-to, vendor quote, print),
// so there is one place a PO is worked on, reached from two lists.
const money = n => (n == null || n === '') ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = s => (s ? String(s).slice(0, 10) : '');
const shortDate = s => { const d = day(s); if (!d) return ''; const x = new Date(d + 'T12:00:00'); return isNaN(x) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: x.getFullYear() === new Date().getFullYear() ? undefined : '2-digit' }); };
const todayYmd = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const daysUntil = ymd => Math.round((new Date(ymd + 'T12:00:00') - new Date(todayYmd() + 'T12:00:00')) / 86400000);

// How a ship date reads: late if it has passed and the material isn't in, "in N days"
// when it is close. Once the PO is Received the date is just the record of it.
export function shipInfo(po) {
  const base = shipInfoBase(po);
  // What Wedco said, once a PM OK'd it (Oct 4 2026): "2 of 4 shipped", or "from Nov 6" when
  // the lines go out on different days.
  if (po.supplier_lines_total) {
    const w = po.supplier_lines_shipped >= po.supplier_lines_total ? 'Wedco: all shipped'
      : `Wedco: ${po.supplier_lines_shipped || 0} of ${po.supplier_lines_total} shipped${po.supplier_est_first && po.supplier_est_first !== po.supplier_est_last ? ` · from ${shortDate(po.supplier_est_first)}` : ''}`;
    return { ...base, sub: [base.sub, w].filter(Boolean).join(' · ') };
  }
  return base;
}
function shipInfoBase(po) {
  const d = day(po.ship_date);
  if (!d) return { text: '—', tone: '#c4c4c4', sub: '' };
  const received = String(po.status || '').toLowerCase() === 'received';
  const n = daysUntil(d);
  if (received) return { text: shortDate(d), tone: '#6b7280', sub: 'received' };
  if (n < 0) return { text: shortDate(d), tone: '#b91c1c', sub: `${-n} day${n === -1 ? '' : 's'} past — not received`, bold: true };
  if (n === 0) return { text: shortDate(d), tone: '#b45309', sub: 'today', bold: true };
  if (n <= 7) return { text: shortDate(d), tone: '#b45309', sub: `in ${n} day${n === 1 ? '' : 's'}`, bold: true };
  return { text: shortDate(d), tone: '#111', sub: '' };
}

const COLS = [
  { key: 'po',     label: 'PO #',        get: p => Number(p.po_number) || 0, dir: -1 },
  { key: 'desc',   label: 'Description', get: p => String(p.description || '').toLowerCase() },
  { key: 'vendor', label: 'Vendor',      get: p => String(p.vendor || '').toLowerCase() },
  { key: 'cost',   label: 'Cost',        get: p => Number(p.cost) || 0, dir: -1, money: true },
  { key: 'who',    label: 'Written by',  get: p => String(p.created_by_name || '').toLowerCase() },
  { key: 'date',   label: 'Date',        get: p => String(p.created_at || ''), dir: -1 },
  // No ship date sorts to the bottom either way up — an empty cell is not "soonest".
  { key: 'ship',   label: 'Ship date',   get: p => day(p.ship_date), emptyLast: true },
  { key: 'status', label: 'Status',      get: p => String(p.status || '').toLowerCase() },
];

export default function JobPoTable({ projectId, canSeeMoney = true, openItemId = null }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState('');
  const [majorOnly, setMajorOnly] = useState(false);
  const [majorFirst, setMajorFirst] = useState(true);
  const [sort, setSort] = useState({ key: 'po', flip: false });
  const [editing, setEditing] = useState(null);
  const [opened, setOpened] = useState(false);

  const load = useCallback(() => {
    apiFetch(`/projects/${projectId}/purchase-orders`)
      .then(r => { setRows(Array.isArray(r) ? r : []); setFailed(false); })
      .catch(() => { setRows([]); setFailed(true); });
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiFetch('/vendors').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {}); }, []);
  // Arriving from a PO tapped on the small tile: open that one.
  useEffect(() => {
    if (opened || !openItemId || !rows) return;
    const po = rows.find(p => String(p.id) === String(openItemId));
    if (po) { setEditing(po); setOpened(true); }
  }, [rows, openItemId, opened]);

  const cols = useMemo(() => COLS.filter(c => canSeeMoney || !c.money), [canSeeMoney]);
  const majorCount = (rows || []).filter(p => p.is_major).length;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = (rows || []).filter(p => (!majorOnly || p.is_major) && (!needle ||
      [p.po_number, p.description, p.notes, p.vendor, p.created_by_name, p.status, p.ship_to].some(v => String(v || '').toLowerCase().includes(needle))));
    const c = COLS.find(x => x.key === sort.key) || COLS[0];
    const dir = (c.dir || 1) * (sort.flip ? -1 : 1);
    list = [...list].sort((a, b) => {
      if (majorFirst && !!a.is_major !== !!b.is_major) return a.is_major ? -1 : 1;
      const x = c.get(a), y = c.get(b);
      if (c.emptyLast && (!x || !y) && x !== y) return !x ? 1 : -1;
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
    return list;
  }, [rows, q, majorOnly, majorFirst, sort]);

  if (rows == null) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;

  const total = shown.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const chip = (on, tone, soft, edge) => ({ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${on ? tone : edge}`, background: on ? tone : soft, color: on ? '#fff' : tone, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' });
  const th = { padding: '9px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', position: 'sticky', top: 0 };
  const td = { padding: '11px 12px', fontSize: 13, color: '#374151', verticalAlign: 'top', borderTop: '1px solid #f1f1f1' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${rows.length} PO${rows.length === 1 ? '' : 's'}…`}
          style={{ fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', flex: '1 1 240px', minWidth: 160 }} />
        <button onClick={() => setMajorOnly(v => !v)} title="Show only the Major POs" style={chip(majorOnly, '#b91c1c', '#fef2f2', '#fca5a5')}>⭐ Major only ({majorCount})</button>
        <button onClick={() => setMajorFirst(v => !v)} title="Keep the Major POs at the top of the list, whatever it is sorted by" style={chip(majorFirst, '#1e3a5f', '#eff6ff', '#bfdbfe')}>Major first</button>
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {shown.length === rows.length ? `${rows.length} PO${rows.length === 1 ? '' : 's'}` : `${shown.length} of ${rows.length}`}{canSeeMoney && shown.length > 0 ? ` · ${money(total)}` : ''}
        </span>
      </div>

      {failed && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 10 }}>The purchase orders didn’t load — check your connection and reopen this.</div>}
      {shown.length === 0 ? (
        <div style={{ fontSize: 13, color: '#9ca3af', padding: '10px 2px' }}>
          {rows.length === 0 ? 'No POs on this job.' : majorOnly && !q ? 'No Major POs on this job. Open a PO and tick “This is a Major PO” to mark one.' : 'Nothing matches that.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                {cols.map(c => {
                  const on = sort.key === c.key;
                  return (
                    <th key={c.key} style={{ ...th, color: on ? '#c2410c' : th.color }} title="Click to sort; click again to reverse"
                      onClick={() => setSort(s => (s.key === c.key ? { key: c.key, flip: !s.flip } : { key: c.key, flip: false }))}>
                      {c.label}{on ? (((c.dir || 1) * (sort.flip ? -1 : 1)) === 1 ? ' ↑' : ' ↓') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {shown.map(po => {
                const sc = PO_STATUS_COLORS[po.status] || { bg: '#f3f4f6', text: '#6b7280' };
                const ship = shipInfo(po);
                return (
                  <tr key={po.id} onClick={() => setEditing(po)} style={{ cursor: 'pointer', background: po.is_major ? '#fffafa' : '#fff' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.background = po.is_major ? '#fffafa' : '#fff'; }}>
                    <td style={{ ...td, fontWeight: 800, color: '#c2410c', whiteSpace: 'nowrap' }}>
                      #{po.po_number}
                      {!!po.is_major && <span title="Major PO" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>⭐ MAJOR</span>}
                      {!!po.is_major && !!po.ndot_certs && <span title="NDOT certifications required" style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, color: '#1e40af', background: '#dbeafe', border: '1px solid #93c5fd', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>NDOT CERTS</span>}
                      {po.source === 'trello' && <span title="Written in Trello, mirrored here" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#0079bf', background: '#eaf5fb', border: '1px solid #b6e0f5', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>TRELLO</span>}
                      {!!po.is_rental && <span title="Rented equipment" style={{ marginLeft: 6, fontSize: 11 }}>🔑</span>}
                    </td>
                    <td style={{ ...td, minWidth: 220, maxWidth: 380 }}>
                      <div style={{ fontWeight: 600, color: '#111', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{po.description || '—'}</div>
                      {po.notes && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{po.notes}</div>}
                      {(po.equipment_asset || po.equipment_name) && <div style={{ fontSize: 12, color: '#9a3412', fontWeight: 700, marginTop: 3 }}>🔧 {po.equipment_asset || po.equipment_name}</div>}
                      {po.ship_to && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>Ship to: {String(po.ship_to).split('\n')[0]}</div>}
                      {po.vendor_quote_filename && <div style={{ fontSize: 11.5, color: '#6d28d9', marginTop: 3 }}>📎 vendor quote attached</div>}
                      <PoShipBits po={po} compact />
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{po.vendor || '—'}</td>
                    {canSeeMoney && <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{money(po.cost)}</td>}
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#6b7280' }}>{po.created_by_name || [po.first_name, po.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#6b7280' }}>{shortDate(po.created_at)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: ship.bold ? 800 : 600, color: ship.tone }}>{ship.text}</div>
                      {ship.sub && <div style={{ fontSize: 11, color: ship.tone === '#111' || ship.tone === '#6b7280' ? '#9ca3af' : ship.tone, marginTop: 2 }}>{ship.sub}</div>}
                    </td>
                    <td style={td}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', background: sc.bg, color: sc.text }}>{po.status || '—'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 8 }}>Click a PO to open it — status, ship date, ship-to, vendor quote and print are all in there. Click a column heading to sort.</div>

      {/* The shared PO window, lifted above the opened-out pillow it was called from. */}
      {editing && createPortal(
        <div style={{ position: 'relative', zIndex: 6000 }}>
          <PoDetailModal po={editing} vendors={vendors} onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }} onDeleted={() => { setEditing(null); load(); }} />
        </div>, document.body)}
    </div>
  );
}
